import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';

import ModalContent from 'components/Granel/new-notas/Modal';
import type { InvoiceModalProps } from 'components/Granel/new-notas/Modal/types';
import Loading from 'components/layout/loading';
import Navbar from 'components/layout/NavbarNew';
import UserMenu from 'components/layout/UserMenu';
import TourBalloon from 'newComponents/NewTour/TourBallon';
import { SelectionBar } from 'pages/BankSlip/components';
import { Pagination } from 'design-system';
import api from 'services/api';
import { useTour } from 'hooks/useTour';
import { verifyUser } from 'utils/functions';
import scrollBlock from 'utils/functions/scrollBlock';
import { insertScrollTop } from 'utils/scroll';

import { FiltersBar, DownloadModal, EmailModal, InvoiceTable } from './components';
import { isValidEmail } from './components/EmailModal';
import {
  bankSlipsFromResponse,
  buildInvoicesUrl,
  getEmptyMessage,
  isInvoiceDownloadable,
  PAGE_SIZE,
} from './helpers';
import {
  buildSearchFromFilters,
  filterRowsByAmount,
  getEmissionMonthOptions,
  hasActiveFiltersInSearch,
  isAmountReadyToApply,
  isSameAmountFilter,
  readApiFiltersFromSearch,
  readFiltersFromSearch,
  shouldClearAmountFilter,
  withoutAmountApiFilters,
} from './filterParams';
import {
  downloadInvoicePdf,
  downloadInvoiceXml,
  emailInvoice,
} from './invoiceActions';
import {
  formatSelectedTotal,
  ORDER_BY_MAP,
  parseOrderBy,
  type InvoiceSortKey,
} from './invoiceDisplay';
import {
  BoxPadding,
  HeaderPage,
  Scrollbar,
  Subtitle,
  Title,
  TitleBlock,
} from './styles';
import {
  ContentCard,
  FiltersSection,
  PaginationWrap,
  SelectionBarWrap,
  TableArea,
  TableWrap,
} from './pageStyles';
import type { DocumentFormat } from 'pages/shared/components/SecondCopyModal';
import type {
  BankSlipItem,
  DownloadMethod,
  Installment,
  Invoice,
} from './types';

const InvoiceModalContent =
  ModalContent as React.ComponentType<InvoiceModalProps>;

const Notas = (): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const showDesktopLayout = useMediaQuery({ minWidth: 993 });
  const showMobileMode = useMediaQuery({ maxWidth: 768 });

  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [bankSlips, setBankSlips] = useState(bankSlipsFromResponse([]));
  const [orderBy, setOrderBy] = useState('emissionDate DESC');
  const [pageTotal, setPageTotal] = useState(0);
  const [invoicesForDownload, setInvoicesForDownload] = useState<string[]>([]);
  const [selectAllInvoices, setSelectAllInvoices] = useState(false);
  const [modalPayment, setModalPayment] = useState<Invoice>();
  const [modalInstallments, setModalInstallments] = useState<Installment>();
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalTargets, setEmailModalTargets] = useState<BankSlipItem[]>([]);
  const [emailModalEmail, setEmailModalEmail] = useState('');
  const [emailModalFormat, setEmailModalFormat] = useState<DocumentFormat>('PDF');
  const [emailModalLoading, setEmailModalLoading] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadModalTargets, setDownloadModalTargets] = useState<BankSlipItem[]>([]);
  const [downloadModalFormat, setDownloadModalFormat] = useState<DocumentFormat>('PDF');
  const [downloadModalLoading, setDownloadModalLoading] = useState(false);

  const { showTour, tourType, tourStep, setTourType, setSteps } = useTour();

  const filterValues = useMemo(
    () => readFiltersFromSearch(location.search),
    [location.search]
  );
  const emissionMonthOptions = useMemo(
    () => getEmissionMonthOptions(filterValues.emissionMonth),
    [filterValues.emissionMonth]
  );
  const hasFilter = hasActiveFiltersInSearch(location.search);
  const showInvoicesTour =
    showTour && tourType === 'invoices' && tourStep === 1;
  const hasSelectedInvoices = invoicesForDownload.length > 0;
  const tableSort = parseOrderBy(orderBy);
  const expandCardForEmptyFilter =
    hasFilter && !loading && bankSlips.length === 0;

  const [invoiceNumberInput, setInvoiceNumberInput] = useState(
    filterValues.invoiceNumber
  );
  const [amountInput, setAmountInput] = useState(filterValues.amount);
  const skipAmountSyncRef = useRef(false);
  const amountFocusedRef = useRef(false);
  const amountFilteredRowsRef = useRef<BankSlipItem[]>([]);
  const allRowsCacheRef = useRef<BankSlipItem[]>([]);
  const allRowsCacheKeyRef = useRef('');
  const requestSequenceRef = useRef(0);
  const amountFilterTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setInvoiceNumberInput(filterValues.invoiceNumber);
  }, [filterValues.invoiceNumber]);

  useEffect(() => {
    if (skipAmountSyncRef.current) {
      skipAmountSyncRef.current = false;
      return;
    }

    if (amountFocusedRef.current) return;

    setAmountInput(filterValues.amount);
  }, [filterValues.amount]);

  const applyFilters = useCallback(
    (next: Partial<typeof filterValues>) => {
      const merged = { ...filterValues, ...next };
      const query = buildSearchFromFilters(merged);

      history.push(query ? `/notas?${query}` : '/notas');
      setActivePage(1);
    },
    [filterValues, history]
  );

  useEffect(() => {
    setActivePage(1);
    setSelectAllInvoices(false);
    setInvoicesForDownload([]);
  }, [location.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (invoiceNumberInput === filterValues.invoiceNumber) return;
      applyFilters({ invoiceNumber: invoiceNumberInput.trim() });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [invoiceNumberInput, filterValues.invoiceNumber, applyFilters]);

  const applyAmountFilter = useCallback(
    (value: string) => {
      if (shouldClearAmountFilter(value)) {
        if (amountInput !== '') {
          setAmountInput('');
        }

        amountFilteredRowsRef.current = [];
        allRowsCacheRef.current = [];
        allRowsCacheKeyRef.current = '';

        if (!filterValues.amount) return;

        skipAmountSyncRef.current = true;
        applyFilters({ amount: '' });
        return;
      }

      if (
        !isAmountReadyToApply(value) ||
        isSameAmountFilter(value, filterValues.amount)
      ) {
        return;
      }

      skipAmountSyncRef.current = true;
      applyFilters({ amount: value });
    },
    [amountInput, filterValues.amount, applyFilters]
  );

  const paginateAmountRows = useCallback(
    (rows: BankSlipItem[], page: number) => {
      const start = (page - 1) * PAGE_SIZE;
      setBankSlips(rows.slice(start, start + PAGE_SIZE));
      setPageTotal(Math.ceil(rows.length / PAGE_SIZE) || 0);
    },
    []
  );

  const applyLocalAmountFilter = useCallback(
    (value: string, page = 1) => {
      if (shouldClearAmountFilter(value)) {
        amountFilteredRowsRef.current = [];
        return false;
      }

      const filteredRows = filterRowsByAmount(allRowsCacheRef.current, value);
      amountFilteredRowsRef.current = filteredRows;
      paginateAmountRows(filteredRows, page);
      return true;
    },
    [paginateAmountRows]
  );

  const fetchAllRowsForAmountFilter = useCallback(
    async (requestId: number) => {
      const cacheKey = `${orderBy}|${filterValues.invoiceNumber}|${filterValues.emissionMonth}`;

      if (
        allRowsCacheRef.current.length > 0 &&
        allRowsCacheKeyRef.current === cacheKey
      ) {
        return allRowsCacheRef.current;
      }

      const token = localStorage.getItem('@UltragazEmpresarial:accessToken');
      if (!token) {
        verifyUser({});
        return null;
      }

      const customerCode = localStorage.getItem(
        '@UltragazEmpresarial:customerCode'
      );
      const customerSiteCode = localStorage.getItem(
        '@UltragazEmpresarial:customerSiteCode'
      );
      const apiFilters = withoutAmountApiFilters(
        readApiFiltersFromSearch(location.search)
      );
      const url = buildInvoicesUrl(
        customerCode,
        customerSiteCode,
        1,
        orderBy,
        apiFilters,
        { bulkFetch: true }
      );

      const res = await api.get(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          access_token: token,
        },
      } as Parameters<typeof api.get>[1]);

      if (requestId !== requestSequenceRef.current) {
        return null;
      }

      if (res.status !== 200) {
        return null;
      }

      const rows = bankSlipsFromResponse(res.data as Invoice[]);
      allRowsCacheRef.current = rows;
      allRowsCacheKeyRef.current = cacheKey;
      return rows;
    },
    [filterValues.emissionMonth, filterValues.invoiceNumber, location.search, orderBy]
  );

  const clearAmountFilterTimer = useCallback(() => {
    if (amountFilterTimerRef.current !== null) {
      window.clearTimeout(amountFilterTimerRef.current);
      amountFilterTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!amountFocusedRef.current) return undefined;

    clearAmountFilterTimer();

    amountFilterTimerRef.current = window.setTimeout(async () => {
      amountFilterTimerRef.current = null;

      if (!amountFocusedRef.current) return;

      if (shouldClearAmountFilter(amountInput)) {
        if (filterValues.amount) {
          applyAmountFilter('');
        }
        return;
      }

      const requestId = ++requestSequenceRef.current;
      setLoading(true);

      try {
        const rows = await fetchAllRowsForAmountFilter(requestId);
        if (!rows || requestId !== requestSequenceRef.current) return;

        applyLocalAmountFilter(amountInput, 1);
        setActivePage(1);
      } finally {
        if (requestId === requestSequenceRef.current) {
          setLoading(false);
        }
      }
    }, 350);

    return clearAmountFilterTimer;
  }, [
    amountInput,
    applyAmountFilter,
    applyLocalAmountFilter,
    clearAmountFilterTimer,
    fetchAllRowsForAmountFilter,
    filterValues.amount,
  ]);

  const handleAmountFocus = useCallback(() => {
    amountFocusedRef.current = true;
  }, []);

  const handleAmountBlur = useCallback(() => {
    clearAmountFilterTimer();
    requestSequenceRef.current += 1;
    amountFocusedRef.current = false;
    applyAmountFilter(amountInput);
  }, [amountInput, applyAmountFilter, clearAmountFilterTimer]);

  const requestInvoices = useCallback(() => {
    if (amountFocusedRef.current && amountInput.trim()) return;

    const apiFilters = readApiFiltersFromSearch(location.search);
    const amountFilterValue = filterValues.amount;
    const usesClientAmountFilter = Boolean(
      amountFilterValue && !shouldClearAmountFilter(amountFilterValue)
    );

    if (usesClientAmountFilter && allRowsCacheRef.current.length > 0) {
      const filteredRows = filterRowsByAmount(
        allRowsCacheRef.current,
        amountFilterValue
      );
      amountFilteredRowsRef.current = filteredRows;
      paginateAmountRows(filteredRows, activePage);
      setLoading(false);
      return;
    }

    const requestId = ++requestSequenceRef.current;
    setLoading(true);

    const token = localStorage.getItem('@UltragazEmpresarial:accessToken');

    if (!token) {
      verifyUser({});
      setLoading(false);
      return;
    }

    const customerCode = localStorage.getItem(
      '@UltragazEmpresarial:customerCode'
    );
    const customerSiteCode = localStorage.getItem(
      '@UltragazEmpresarial:customerSiteCode'
    );

    const fetchAndApply = async (): Promise<void> => {
      try {
        if (usesClientAmountFilter) {
          const rows = await fetchAllRowsForAmountFilter(requestId);
          if (!rows || requestId !== requestSequenceRef.current) return;

          const filteredRows = filterRowsByAmount(rows, amountFilterValue);
          amountFilteredRowsRef.current = filteredRows;
          paginateAmountRows(filteredRows, activePage);
          return;
        }

        amountFilteredRowsRef.current = [];
        allRowsCacheRef.current = [];
        allRowsCacheKeyRef.current = '';

        const url = buildInvoicesUrl(
          customerCode,
          customerSiteCode,
          activePage,
          orderBy,
          apiFilters
        );

        const res = await api.get(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            access_token: token,
          },
        } as Parameters<typeof api.get>[1]);

        if (requestId !== requestSequenceRef.current) return;

        if (res.status !== 200) return;

        setPageTotal(Number(res.headers['total-pages']) || 0);
        setBankSlips(bankSlipsFromResponse(res.data as Invoice[]));
      } catch (err: unknown) {
        if (requestId !== requestSequenceRef.current) return;

        const error = err as { response?: { status?: number; data?: { message?: string } } };
        if (error.response?.status === 401) return;

        const message = error.response?.data?.message;
        toast.error(message ?? 'Ocorreu um erro interno na aplicação');
      } finally {
        if (requestId === requestSequenceRef.current) {
          setLoading(false);
        }
      }
    };

    void fetchAndApply();
  }, [
    activePage,
    amountInput,
    fetchAllRowsForAmountFilter,
    filterValues.amount,
    filterValues.emissionMonth,
    filterValues.invoiceNumber,
    location.search,
    orderBy,
    paginateAmountRows,
  ]);

  useEffect(() => {
    requestInvoices();
  }, [requestInvoices]);

  useEffect(() => {
    setTourType('invoices');
    setSteps(1);
  }, [setTourType, setSteps]);

  const clearSelection = () => {
    setSelectAllInvoices(false);
    setInvoicesForDownload([]);
  };

  const toggleSelectAllInvoices = () => {
    if (selectAllInvoices) {
      clearSelection();
      return;
    }

    setSelectAllInvoices(true);
    setInvoicesForDownload(
      bankSlips
        .filter(({ invoice }) => isInvoiceDownloadable(invoice))
        .map(({ invoice }) => invoice.invoiceId)
    );
  };

  const toggleInvoiceSelection = (invoiceId: string) => {
    const row = bankSlips.find(({ invoice }) => invoice.invoiceId === invoiceId);
    if (!row || !isInvoiceDownloadable(row.invoice)) return;

    if (selectAllInvoices) {
      setSelectAllInvoices(false);
      setInvoicesForDownload([invoiceId]);
      return;
    }

    setInvoicesForDownload((current) =>
      current.includes(invoiceId)
        ? current.filter((id) => id !== invoiceId)
        : [...current, invoiceId]
    );
  };

  const requestDownload = (
    method: DownloadMethod,
    invoiceIds: string[],
    options?: { email?: string; format?: DocumentFormat }
  ): Promise<void> => {
    setLoading(true);

    const token = localStorage.getItem('@UltragazEmpresarial:accessToken');
    const customerCode = localStorage.getItem(
      '@UltragazEmpresarial:customerCode'
    );
    const customerSiteCode = localStorage.getItem(
      '@UltragazEmpresarial:customerSiteCode'
    );
    const email =
      options?.email ??
      localStorage.getItem('@UltragazEmpresarial:loginEmail');
    const documentType = options?.format === 'XML' ? 'XML' : 'DANFE';

    const body = {
      customerCode,
      customerSiteCode,
      deliveryMethod: method,
      email,
      invoice: {
        documentType,
        ids: invoiceIds,
      },
    };

    return api
      .post(
        '/industrial/invoice/v1/duplicates',
        body,
        {
          method: 'POST',
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            access_token: token,
          },
        } as Parameters<typeof api.post>[2]
      )
      .then((response) => {
        if (method === 'DOWNLOAD') {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `ultra_${body.invoice.ids.length}_notas_fiscais.zip`
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          toast.success('Notas baixadas. Confira sua pasta de downloads.');
        } else {
          toast.success('As notas foram enviadas para o seu e-mail.');
        }

        clearSelection();
      })
      .catch((err) => {
        clearSelection();
        toast.error(
          err.message ?? 'Ocorreu um erro ao processar a solicitação'
        );
        throw err;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableSort = (key: InvoiceSortKey) => {
    const current = parseOrderBy(orderBy);
    const direction =
      current.key === key && current.direction === 'desc' ? 'asc' : 'desc';

    setOrderBy(ORDER_BY_MAP[key][direction]);
    setActivePage(1);
  };

  const handleShow = useCallback((invoice: Invoice, installment: Installment) => {
    setModalPayment(invoice);
    setModalInstallments(installment);
    setShowModal(true);
  }, []);

  const handleRowView = useCallback(
    (row: BankSlipItem) => {
      handleShow(row.invoice, row.installment);
    },
    [handleShow]
  );

  const getSelectedRows = (): BankSlipItem[] =>
    bankSlips.filter(({ invoice }) =>
      invoicesForDownload.includes(invoice.invoiceId)
    );

  const getModalTotalAmount = (targets: BankSlipItem[]): string =>
    formatSelectedTotal(
      targets.map(({ installment }) => installment.amountOriginal ?? 0)
    );

  const openDownloadModal = (targets: BankSlipItem[]) => {
    if (targets.length === 0) return;

    setDownloadModalTargets(targets);
    setDownloadModalFormat('PDF');
    setDownloadModalOpen(true);
  };

  const openEmailModal = (targets: BankSlipItem[]) => {
    if (targets.length === 0) return;

    setEmailModalTargets(targets);
    setEmailModalEmail(
      localStorage.getItem('@UltragazEmpresarial:loginEmail') ?? ''
    );
    setEmailModalFormat('PDF');
    setEmailModalOpen(true);
  };

  const handleRowDownload = (row: BankSlipItem) => {
    openDownloadModal([row]);
  };

  const handleRowEmail = (row: BankSlipItem) => {
    openEmailModal([row]);
  };

  const handleDownloadModalClose = () => {
    if (downloadModalLoading) return;

    setDownloadModalOpen(false);
    setDownloadModalTargets([]);
  };

  const handleDownloadModalSubmit = async () => {
    if (downloadModalTargets.length === 0) return;

    setDownloadModalLoading(true);

    try {
      if (downloadModalTargets.length === 1) {
        const invoiceId = downloadModalTargets[0].invoice.invoiceId;

        if (downloadModalFormat === 'XML') {
          await downloadInvoiceXml(invoiceId);
        } else {
          await downloadInvoicePdf(invoiceId);
        }
      } else {
        await requestDownload(
          'DOWNLOAD',
          downloadModalTargets.map(({ invoice }) => invoice.invoiceId),
          { format: downloadModalFormat }
        );
      }

      setDownloadModalOpen(false);
      setDownloadModalTargets([]);
    } catch {
      if (downloadModalTargets.length === 1) {
        toast.error('Ocorreu um erro ao baixar a nota.');
      }
    } finally {
      setDownloadModalLoading(false);
    }
  };

  const handleEmailModalClose = () => {
    if (emailModalLoading) return;

    setEmailModalOpen(false);
    setEmailModalTargets([]);
  };

  const handleEmailModalSubmit = async () => {
    if (emailModalTargets.length === 0 || !isValidEmail(emailModalEmail)) {
      return;
    }

    setEmailModalLoading(true);

    try {
      if (emailModalTargets.length === 1) {
        await emailInvoice(emailModalTargets[0].invoice.invoiceId, {
          email: emailModalEmail.trim(),
          format: emailModalFormat,
        });
      } else {
        await requestDownload(
          'EMAIL',
          emailModalTargets.map(({ invoice }) => invoice.invoiceId),
          {
            email: emailModalEmail.trim(),
            format: emailModalFormat,
          }
        );
      }

      setEmailModalOpen(false);
      setEmailModalTargets([]);
    } catch {
      toast.error('Ocorreu um erro ao enviar a nota por e-mail.');
    } finally {
      setEmailModalLoading(false);
    }
  };

  const handleChangePage = (pageNumber: number) => {
    setActivePage(pageNumber);
    insertScrollTop(window.innerWidth <= 455 ? 220 : 200);
  };

  const toggleSteps = () => {
    scrollBlock('#outer-container', 'body', true);
  };

  const selectedTotalLabel = formatSelectedTotal(
    bankSlips
      .filter(({ invoice }) => invoicesForDownload.includes(invoice.invoiceId))
      .map(({ installment }) => installment.amountOriginal ?? 0)
  );

  const emptyMessage = getEmptyMessage(loading, hasFilter);

  return (
    <>
      <Navbar selected="notas" withTable>
        <UserMenu intro={toggleSteps} />
        <Scrollbar>
          <BoxPadding>
            <HeaderPage>
              <TitleBlock>
                <Title>Minhas Notas</Title>
                <Subtitle>Acompanhe suas notas fiscais</Subtitle>
              </TitleBlock>
            </HeaderPage>
          </BoxPadding>

          <ContentCard $expandWhenEmpty={expandCardForEmptyFilter}>
            <FiltersSection>
              <FiltersBar
                invoiceNumberValue={invoiceNumberInput}
                emissionMonthValue={filterValues.emissionMonth}
                amountValue={amountInput}
                emissionMonthOptions={emissionMonthOptions}
                onInvoiceNumberChange={(value) =>
                  setInvoiceNumberInput(value.replace(/\D/g, ''))
                }
                onEmissionMonthChange={(value) =>
                  applyFilters({ emissionMonth: value })
                }
                onAmountChange={setAmountInput}
                onAmountFocus={handleAmountFocus}
                onAmountBlur={handleAmountBlur}
              />
            </FiltersSection>

            {hasSelectedInvoices && (
              <SelectionBarWrap>
                <SelectionBar
                  selectedCount={invoicesForDownload.length}
                  totalLabel={selectedTotalLabel}
                  itemLabelSingular="nota"
                  itemLabelPlural="notas"
                  sendEmailLabel="Enviar por e-mail"
                  downloadLabel="Baixar notas"
                  onDownload={() => openDownloadModal(getSelectedRows())}
                  onSendEmail={() => openEmailModal(getSelectedRows())}
                  disabled={loading}
                />
              </SelectionBarWrap>
            )}

            <TableWrap $showTour={showInvoicesTour}>
              <TableArea $expandWhenEmpty={expandCardForEmptyFilter}>
                <InvoiceTable
                  rows={bankSlips}
                  selectedIds={invoicesForDownload}
                  selectAllChecked={selectAllInvoices}
                  sortKey={tableSort.key}
                  sortDirection={tableSort.direction}
                  emptyMessage={emptyMessage}
                  compact={!showDesktopLayout}
                  onToggleSelectAll={toggleSelectAllInvoices}
                  onToggleRow={toggleInvoiceSelection}
                  onSort={handleTableSort}
                  onView={handleRowView}
                  onDownload={handleRowDownload}
                  onSendEmail={handleRowEmail}
                />
              </TableArea>

              {showInvoicesTour && (
                <TourBalloon
                  title="Notas fiscais"
                  content="Aqui você encontra todas suas notas fiscais. Você poderá baixá-las, imprimí-las ou enviá-las por e-mail."
                  position={{
                    left: '50%',
                    right: '0',
                    top: '-187px',
                    arrowPosition: 'bottom',
                    maxWidth: 'unset',
                    boxShadow: false,
                    width: '492px',
                    transform: 'translateX(-50%)',
                  }}
                />
              )}
            </TableWrap>

            {pageTotal > 0 && (
              <PaginationWrap>
                <Pagination
                  page={activePage}
                  totalPages={pageTotal}
                  onPageChange={handleChangePage}
                  disabled={loading}
                  activeBackgroundColor="#0B29FB"
                  showFirstLast
                />
              </PaginationWrap>
            )}
          </ContentCard>
        </Scrollbar>
      </Navbar>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        dialogClassName="modal-90w"
        aria-labelledby="example-custom-modal-styling-title"
        animation={false}
        centered
      >
        <InvoiceModalContent
          close={() => setShowModal(false)}
          payment={modalPayment}
          installment={modalInstallments}
          showMobileMode={showMobileMode}
        />
      </Modal>

      <DownloadModal
        open={downloadModalOpen}
        invoiceNumber={
          downloadModalTargets.length === 1
            ? downloadModalTargets[0].invoice.invoiceNumber
            : undefined
        }
        itemCount={downloadModalTargets.length}
        totalAmount={getModalTotalAmount(downloadModalTargets)}
        loading={downloadModalLoading}
        format={downloadModalFormat}
        onFormatChange={setDownloadModalFormat}
        onClose={handleDownloadModalClose}
        onSubmit={handleDownloadModalSubmit}
      />

      <EmailModal
        open={emailModalOpen}
        email={emailModalEmail}
        invoiceNumber={
          emailModalTargets.length === 1
            ? emailModalTargets[0].invoice.invoiceNumber
            : undefined
        }
        itemCount={emailModalTargets.length}
        totalAmount={getModalTotalAmount(emailModalTargets)}
        loading={emailModalLoading}
        format={emailModalFormat}
        onFormatChange={setEmailModalFormat}
        onEmailChange={setEmailModalEmail}
        onClose={handleEmailModalClose}
        onSubmit={handleEmailModalSubmit}
      />

      {loading && <Loading />}
    </>
  );
};

export default Notas;
